"use client";

/**
 * Theme Provider
 *
 * Wraps the next-themes ThemeProvider for light/dark mode support
 * Automatically applies saved color theme and high contrast mode on mount
 */

import * as React from "react";
import { ThemeProvider as NextThemesProvider } from "next-themes";
import { type ThemeProviderProps } from "next-themes/dist/types";
import { initializeTheme, initializeHighContrastMode, getStoredHighContrastMode, setStoredHighContrastMode, applyHighContrastMode, initializeFontSize, getStoredFontSize, setStoredFontSize, applyFontSize, initializeReducedMotionMode, getStoredReducedMotionMode, setStoredReducedMotionMode, applyReducedMotionMode } from "@/lib/theme/theme-utils";
import { HighContrastMode, FontSize, ReducedMotionMode } from "@/types/theme";

interface ThemeContextValue {
  highContrastMode: HighContrastMode;
  setHighContrastMode: (mode: HighContrastMode) => void;
  fontSize: FontSize;
  setFontSize: (size: FontSize) => void;
  reducedMotionMode: ReducedMotionMode;
  setReducedMotionMode: (mode: ReducedMotionMode) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | undefined>(undefined);

export function useHighContrastMode() {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useHighContrastMode must be used within ThemeProvider");
  }
  return { highContrastMode: context.highContrastMode, setHighContrastMode: context.setHighContrastMode };
}

export function useFontSize() {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useFontSize must be used within ThemeProvider");
  }
  return { fontSize: context.fontSize, setFontSize: context.setFontSize };
}

export function useReducedMotionMode() {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useReducedMotionMode must be used within ThemeProvider");
  }
  return { reducedMotionMode: context.reducedMotionMode, setReducedMotionMode: context.setReducedMotionMode };
}

const ThemeProvider = ({ children, ...props }: ThemeProviderProps) => {
  const [mounted, setMounted] = React.useState(false);
  const [highContrastMode, setHighContrastModeState] = React.useState<HighContrastMode>('off');
  const [fontSize, setFontSizeState] = React.useState<FontSize>('medium');
  const [reducedMotionMode, setReducedMotionModeState] = React.useState<ReducedMotionMode>('off');

  // Run immediately on client with useLayoutEffect to prevent flash
  // IMPORTANT: Theme initialization MUST happen before setting mounted=true
  // to prevent FOUC (Flash of Unstyled Content)
  React.useLayoutEffect(() => {
    // First: Initialize color theme from localStorage (synchronous, no await)
    try {
      initializeTheme();
    } catch (error) {
      console.error("Failed to initialize theme, using defaults:", error);
      // Continue with default theme even if initialization fails
    }

    // Second: Initialize high contrast mode from localStorage
    try {
      const storedHighContrast = initializeHighContrastMode();
      setHighContrastModeState(storedHighContrast);
    } catch (error) {
      console.error("Failed to initialize high contrast mode, using default:", error);
      // Continue with default high contrast mode even if initialization fails
    }

    // Third: Initialize font size from localStorage
    try {
      const storedFontSize = initializeFontSize();
      setFontSizeState(storedFontSize);
    } catch (error) {
      console.error("Failed to initialize font size, using default:", error);
      // Continue with default font size even if initialization fails
    }

    // Fourth: Initialize reduced motion mode from localStorage
    try {
      const storedReducedMotion = initializeReducedMotionMode();
      setReducedMotionModeState(storedReducedMotion);
    } catch (error) {
      console.error("Failed to initialize reduced motion mode, using default:", error);
      // Continue with default reduced motion mode even if initialization fails
    }

    // Fifth: Set mounted to true AFTER themes are applied
    // This removes the hiding CSS and allows the component to render
    setMounted(true);
  }, []);

  // Wrapper function to update high contrast mode state and apply it
  const setHighContrastMode = React.useCallback((mode: HighContrastMode) => {
    setHighContrastModeState(mode);
    setStoredHighContrastMode(mode);
    applyHighContrastMode(mode === 'on');
  }, []);

  // Wrapper function to update font size state and apply it
  const setFontSize = React.useCallback((size: FontSize) => {
    setFontSizeState(size);
    setStoredFontSize(size);
    applyFontSize(size);
  }, []);

  // Wrapper function to update reduced motion mode state and apply it
  const setReducedMotionMode = React.useCallback((mode: ReducedMotionMode) => {
    setReducedMotionModeState(mode);
    setStoredReducedMotionMode(mode);
    applyReducedMotionMode(mode === 'on');
  }, []);

  const contextValue = React.useMemo(
    () => ({
      highContrastMode,
      setHighContrastMode,
      fontSize,
      setFontSize,
      reducedMotionMode,
      setReducedMotionMode,
    }),
    [highContrastMode, setHighContrastMode, fontSize, setFontSize, reducedMotionMode, setReducedMotionMode]
  );

  // Use a suppress hydration warning wrapper instead of returning null
  // This prevents the component from unmounting/remounting
  return (
    <ThemeContext.Provider value={contextValue}>
      <NextThemesProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        enableColorScheme={false}
        disableTransitionOnChange
        {...props}
      >
        <div suppressHydrationWarning>
          {children}
        </div>
      </NextThemesProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;